<template>
  <div
    class="flex flex-col gap-[48px]"
    :class="{ invisible: phase === 'measure' }"
  >
    <div
      v-for="(page, pageIdx) in pages"
      :key="pageIdx"
      data-a4-page
      class="relative flex h-[1754px] w-[1240px] bg-white"
      style="-webkit-print-color-adjust: exact"
    >
      <!-- Left sidebar.
           Sprachen and Mobilität live here and are NOT paginated — the sidebar
           is re-rendered per page and never measured. That is safe because both
           lists are bounded: the picker offers 10 languages and rejects
           duplicates, and Mobilität has at most 2 rows. Worst case is
           48 + 256 photo + 142 name (two lines) + 164 Geboren
           + 740 Sprachen (10 rows) + 164 Mobilität = ~1514px of 1754.
           Anything that lifts those bounds
           (a longer language catalogue, a third Mobilität row) has to measure
           the sidebar instead. -->
      <div class="h-full w-[409px] shrink-0 bg-[#f3f3f5] px-[48px] pt-[48px]">
        <!-- Photo -->
        <div
          v-if="hasPhoto"
          class="h-[256px] w-[254px] overflow-hidden bg-gray-200"
        >
          <img
            v-if="profileImageUrl"
            :src="profileImageUrl"
            class="h-full w-full object-cover"
          />
          <image-preview
            v-else
            :uuid="photoUuid"
            :width="PORTFOLIO_PREVIEW_IMAGE_WIDTH"
            class="h-full w-full object-cover"
          />
        </div>

        <!-- Name + cyan underline -->
        <div :class="hasPhoto ? 'mt-[48px]' : ''">
          <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
            {{ account.firstName }}<br />{{ account.lastName }}
          </p>
          <div class="mt-[16px] h-[6px] w-[200px] bg-[#00f9ff]" />
        </div>

        <template v-if="pageIdx === 0">
          <!-- Geboren -->
          <div v-if="hasBirthDetails" class="mt-[48px]">
            <p
              class="text-[21px] font-bold uppercase leading-[32px] text-[#1d1d1f]"
            >
              Geboren
            </p>
            <div class="mt-[6px] flex flex-col gap-[6px]">
              <div v-if="account.birthday" class="flex items-center gap-[9px]">
                <svg-icon
                  name="birthday-cake"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                  {{ formatDate(account.birthday) }}
                </span>
              </div>
              <div
                v-if="account.placeOfBirth"
                class="flex items-center gap-[9px]"
              >
                <svg-icon
                  name="map-marker"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                  {{ account.placeOfBirth }}
                </span>
              </div>
            </div>
          </div>

          <!-- Sprachen -->
          <div v-if="languageSkills.length > 0" class="mt-[48px]">
            <p
              class="text-[21px] font-bold uppercase leading-[32px] text-[#1d1d1f]"
            >
              Sprachen
            </p>
            <div class="mt-[6px] flex flex-col gap-[6px]">
              <div
                v-for="(lang, i) in languageSkills"
                :key="i"
                class="flex items-center gap-[9px]"
              >
                <svg-icon
                  name="translate"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <div class="min-w-0 text-[21px] leading-[30px] text-[#1d1d1f]">
                  <p class="m-0">{{ languageName(lang.code) }}</p>
                  <p v-if="languageLevelShortLabel(lang.level)" class="m-0">
                    {{ languageLevelShortLabel(lang.level) }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobilität -->
          <div v-if="hasMobility" class="mt-[48px]">
            <p
              class="text-[21px] font-bold uppercase leading-[32px] text-[#1d1d1f]"
            >
              Mobilität
            </p>
            <div class="mt-[6px] flex flex-col gap-[6px]">
              <div
                v-if="mobility.hasDriversLicense"
                class="flex items-center gap-[9px]"
              >
                <svg-icon
                  name="car-side"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                  Führerschein Klasse B
                </span>
              </div>
              <div v-if="mobility.hasCar" class="flex items-center gap-[9px]">
                <svg-icon
                  name="car-side"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                  Auto vorhanden
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Right content area -->
      <div class="min-w-0 flex-1 px-[50px] pt-[47px]">
        <!-- Lebenslauf heading + cyan underline + Kontakt (page 1 only).
             Kontakt sits inside the tracked heading element on purpose: its
             height is measured via captureHeights() and feeds availableHeight(),
             so pagination accounts for it. -->
        <div v-if="pageIdx === 0" :ref="trackHeading">
          <p
            class="text-[54px] font-bold leading-[65px] tracking-[-1.1px] text-[#1d1d1f]"
          >
            Lebenslauf
          </p>
          <div class="mt-[24px] h-[6px] w-[200px] bg-[#b8fdff]" />

          <!-- Kontakt -->
          <div class="mt-[48px] flex flex-col gap-[16px]">
            <div v-if="account.phone" class="flex items-start gap-[16px]">
              <svg-icon
                name="phone"
                class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
              />
              <span
                class="whitespace-nowrap text-[24px] leading-[36px] text-[#1d1d1f]"
              >
                {{ account.phone }}
              </span>
            </div>
            <div v-if="addressLine" class="flex items-start gap-[16px]">
              <svg-icon
                name="map-marker"
                class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
              />
              <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                {{ addressLine }}
              </span>
            </div>
            <div v-if="account.email" class="flex items-start gap-[16px]">
              <svg-icon
                name="envelope"
                class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
              />
              <fitted-email
                :ref="trackEmail"
                :email="account.email"
                :full-size="24"
                :floor-size="16"
                class="leading-[36px] text-[#1d1d1f]"
              />
            </div>
          </div>
        </div>

        <!-- Repeated "Lebenslauf" heading for page 2+.
             Also rendered on the measure page (which is always page 0) so
             captureHeights() knows how much room it takes before pagination
             decides what fits on the later pages. -->
        <div
          v-if="pageIdx > 0 || phase === 'measure'"
          :ref="trackContinuationHeading"
        >
          <p
            class="text-[54px] font-bold leading-[65px] tracking-[-1.1px] text-[#1d1d1f]"
          >
            Lebenslauf
          </p>
          <div class="mt-[24px] h-[6px] w-[200px] bg-[#b8fdff]" />
        </div>

        <!-- Sections -->
        <div class="mt-[48px] flex flex-col gap-[48px]">
          <div
            v-for="(block, blockIdx) in page.blocks"
            :key="blockIdx"
            :ref="(el: any) => trackSection(el, blockIdx)"
          >
            <!-- Praktikum -->
            <template v-if="block.type === 'praktikum'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="calendar"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  BERUFSERFAHRUNG
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[24px] pl-[30px]">
                <div
                  v-for="(exp, i) in block.entries"
                  :key="i"
                  :ref="(el: any) => trackEntry(el, blockIdx, i)"
                >
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(exp.from, exp.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ exp.companyName }}
                  </p>
                  <p
                    v-if="jobTitle(exp)"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    {{ jobTitle(exp) }}
                  </p>
                  <template
                    v-for="(descBlock, di) in parseDescriptionBlocks(
                      isDescriptionEnabled ? exp.description : null,
                    )"
                    :key="`desc-${di}`"
                  >
                    <ul
                      v-if="descBlock.type === 'list'"
                      class="list-disc pl-[30px] text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      <li v-for="(item, ii) in descBlock.items" :key="ii">
                        {{ item }}
                      </li>
                    </ul>
                    <p
                      v-else
                      class="whitespace-pre-line text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      {{ descBlock.items.join('\n') }}
                    </p>
                  </template>
                </div>
              </div>
            </template>

            <!-- Praktikum continuation (no heading) -->
            <template v-else-if="block.type === 'praktikum-cont'">
              <div class="flex flex-col gap-[24px] pl-[30px]">
                <div v-for="(exp, i) in block.entries" :key="i">
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(exp.from, exp.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ exp.companyName }}
                  </p>
                  <p
                    v-if="jobTitle(exp)"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    {{ jobTitle(exp) }}
                  </p>
                  <template
                    v-for="(descBlock, di) in parseDescriptionBlocks(
                      isDescriptionEnabled ? exp.description : null,
                    )"
                    :key="`desc-${di}`"
                  >
                    <ul
                      v-if="descBlock.type === 'list'"
                      class="list-disc pl-[30px] text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      <li v-for="(item, ii) in descBlock.items" :key="ii">
                        {{ item }}
                      </li>
                    </ul>
                    <p
                      v-else
                      class="whitespace-pre-line text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      {{ descBlock.items.join('\n') }}
                    </p>
                  </template>
                </div>
              </div>
            </template>

            <!-- Bildungsweg -->
            <template v-else-if="block.type === 'bildungsweg'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="medal"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  BILDUNGSWEG
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[24px] pl-[30px]">
                <div
                  v-for="(entry, i) in block.entries"
                  :key="i"
                  :ref="(el: any) => trackEntry(el, blockIdx, i)"
                >
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(entry.from, entry.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ entry.graduation }}, {{ entry.placeName }}
                  </p>
                  <p
                    v-if="entry.courseName"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    Studiengang {{ entry.courseName }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Bildungsweg continuation (no heading) -->
            <template v-else-if="block.type === 'bildungsweg-cont'">
              <div class="flex flex-col gap-[24px] pl-[30px]">
                <div v-for="(entry, i) in block.entries" :key="i">
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(entry.from, entry.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ entry.graduation }}, {{ entry.placeName }}
                  </p>
                  <p
                    v-if="entry.courseName"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    Studiengang {{ entry.courseName }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Hard Skills -->
            <template v-else-if="block.type === 'hard-skills'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="display"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  HARD SKILLS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <div
                  v-for="(skill, i) in block.entries"
                  :key="i"
                  class="flex items-baseline gap-[24px]"
                >
                  <span
                    class="text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                    >{{ skill.name }}</span
                  >
                  <span
                    v-if="skill.level"
                    class="text-[21px] leading-[30px] text-[#1d1d1f]"
                    >{{ skill.level }}</span
                  >
                </div>
              </div>
            </template>

            <!-- Soft Skills -->
            <template v-else-if="block.type === 'soft-skills'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="brain"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  SOFT SKILLS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <span
                  v-for="(skill, i) in block.entries"
                  :key="i"
                  class="text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                >
                  {{ skill.name }}
                </span>
              </div>
            </template>

            <!-- Hobbies -->
            <template v-else-if="block.type === 'hobbies'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="heart"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  HOBBYS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <span
                  v-for="(hobby, i) in block.entries"
                  :key="i"
                  class="text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                >
                  {{ hobby.name }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- Footer -->
        <p
          class="absolute bottom-[40px] right-[48px] text-[22px] leading-[31px] text-[#1d1d1f]"
        >
          {{ account.firstName }} {{ account.lastName }} | Lebenslauf
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import ImagePreview from '@/components/ImagePreview.vue'
import FittedEmail from './FittedEmail.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import accountStore from '@/stores/account.store'
import { PORTFOLIO_PREVIEW_IMAGE_WIDTH } from './preview-image.constants'
import documentsStore from '@/stores/documents.store'
import mobilityStore from '@/stores/mobility.store'
import { DocumentType } from '@/enums'
import { languageOptions } from '@/assets/data/languages'
import {
  collectEducationEntries,
  sortEducationEntriesByLatestTo,
} from '@/units/ds/utils/education-entries'
import { parseDescriptionBlocks } from '@/units/ds/utils/parse-description-blocks'
import { languageLevelShortLabel } from '@/units/ds/utils/language-level-short-label'
import { sortPracticalExperiencesByLatestTo } from '@/units/ds/utils/sort-practical-experiences'
import { paginateCvBlocks } from '@/units/ds/utils/paginate-cv-blocks'
import {
  CV_ENTRY_GAP,
  CV_SPLITTABLE_BLOCK_TYPES,
} from '@/units/ds/utils/cv-pagination.constants'
import type { CvContentBlock } from '@/units/ds/utils/cv-content-block.type'
import type { CvPage } from '@/units/ds/utils/cv-page.type'
import { Features, FeatureFlags } from '@/modules/features'

const props = withDefaults(
  defineProps<{
    profileImageUrl?: string
  }>(),
  { profileImageUrl: undefined },
)

const account = computed(() => accountStore.state.account)

const isDescriptionEnabled = computed(() =>
  Features.isEnabled(FeatureFlags.WorkExperienceDescription),
)

const addressCityLine = computed(() =>
  [account.value.shippingPostalCode, account.value.shippingCity]
    .filter(Boolean)
    .join(' '),
)

const addressLine = computed(() =>
  [account.value.shippingStreet, addressCityLine.value]
    .filter(Boolean)
    .join(', '),
)

const mobility = computed(() => mobilityStore.state.mobility)

// Only positives are worth printing on a CV — "kein Führerschein" is not a
// selling point. A false answer is treated like an unanswered one, so the
// section disappears entirely unless there is something to state.
const hasMobility = computed(
  () =>
    Boolean(mobility.value.hasDriversLicense) || Boolean(mobility.value.hasCar),
)

const hasBirthDetails = computed(
  () => Boolean(account.value.birthday) || Boolean(account.value.placeOfBirth),
)

mobilityStore.fetchMobility()

const photoUuid = computed(() => {
  const pics = documentsStore.getDocumentsByCategory(DocumentType.Picture)
  return pics.length > 0 ? pics[0].uuid : null
})

const hasPhoto = computed(() => !!props.profileImageUrl || !!photoUuid.value)

const practicalExperiences = computed(() =>
  sortPracticalExperiencesByLatestTo(account.value.practicalExperiences ?? []),
)
const educationEntries = computed(() =>
  sortEducationEntriesByLatestTo(collectEducationEntries(account.value)),
)
const languageSkills = computed(() => account.value.languageSkills ?? [])
const hardSkills = computed(() =>
  (account.value.hardSkills ?? []).filter((s) => s.category === 'software'),
)
const softSkills = computed(() => account.value.softSkills ?? [])
const hobbies = computed(() => account.value.hobbies ?? [])

// --- DOM-measured pagination ---

const PAGE_HEIGHT = 1754
const TOP_PADDING = 47
const FOOTER_RESERVED = 80
const CONTENT_AREA = PAGE_HEIGHT - TOP_PADDING - FOOTER_RESERVED
const HEADING_MARGIN = 48

// Two-phase rendering: 'measure' renders all blocks on one page so we can
// read their real offsetHeight; 'ready' uses the measured values to paginate.
const phase = ref<'measure' | 'ready'>('measure')

let emailComponent: InstanceType<typeof FittedEmail> | null = null
let headingElement: HTMLElement | null = null
let continuationHeadingElement: HTMLElement | null = null
const sectionElMap = new Map<number, HTMLElement>()
const entryElMap = new Map<number, HTMLElement[]>()

const measuredHeadingH = ref(0)
const measuredContinuationHeadingH = ref(0)
const measuredSections = ref<number[]>([])
const measuredEntries = ref<Map<number, number[]>>(new Map())

const trackEmail = (el: unknown) => {
  emailComponent = el as typeof emailComponent
}

const trackHeading = (el: any) => {
  headingElement = el instanceof HTMLElement ? el : null
}

const trackContinuationHeading = (el: any) => {
  if (phase.value !== 'measure' || !(el instanceof HTMLElement)) return
  continuationHeadingElement = el
}

const trackSection = (el: any, blockIdx: number) => {
  if (phase.value !== 'measure' || !(el instanceof HTMLElement)) return
  sectionElMap.set(blockIdx, el)
}

const trackEntry = (el: any, blockIdx: number, entryIdx: number) => {
  if (phase.value !== 'measure' || !(el instanceof HTMLElement)) return
  if (!entryElMap.has(blockIdx)) entryElMap.set(blockIdx, [])
  entryElMap.get(blockIdx)![entryIdx] = el
}

const buildRawBlocks = (): CvContentBlock[] => {
  const blocks: CvContentBlock[] = []

  if (practicalExperiences.value.length > 0) {
    blocks.push({
      type: 'praktikum',
      entries: practicalExperiences.value,
      height: 0,
    })
  }

  if (educationEntries.value.length > 0) {
    blocks.push({
      type: 'bildungsweg',
      entries: educationEntries.value,
      height: 0,
    })
  }

  if (hardSkills.value.length > 0) {
    blocks.push({ type: 'hard-skills', entries: hardSkills.value, height: 0 })
  }

  if (softSkills.value.length > 0) {
    blocks.push({ type: 'soft-skills', entries: softSkills.value, height: 0 })
  }

  if (hobbies.value.length > 0) {
    blocks.push({ type: 'hobbies', entries: hobbies.value, height: 0 })
  }

  return blocks
}

const rawBlocks = computed(() => buildRawBlocks())

// Page 1 carries the "Lebenslauf" heading plus the contact block, later pages
// the bare repeated heading, which is shorter. Both push the sections down by
// HEADING_MARGIN, so both have to come off the usable height — subtracting only
// on page 1 ran page 2 into the footer.
const availableHeight = (pageIdx: number) =>
  CONTENT_AREA -
  (pageIdx === 0
    ? measuredHeadingH.value
    : measuredContinuationHeadingH.value) -
  HEADING_MARGIN

const pages = computed<CvPage[]>(() => {
  const blocks = rawBlocks.value

  if (phase.value === 'measure') {
    return [{ blocks: blocks.map((b) => ({ ...b, height: 0 })) }]
  }

  const measured: CvContentBlock[] = blocks.map((b, i) => {
    const block: CvContentBlock = {
      ...b,
      height: measuredSections.value[i] ?? 0,
    }
    if (
      CV_SPLITTABLE_BLOCK_TYPES.includes(b.type) &&
      measuredEntries.value.has(i)
    ) {
      const eH = measuredEntries.value.get(i)!
      block.entryHeights = eH
      const totalEntryArea =
        eH.reduce((sum, h) => sum + h, 0) +
        Math.max(0, eH.length - 1) * CV_ENTRY_GAP
      block.headerHeight = Math.max(0, block.height - totalEntryArea)
    }
    return block
  })

  return paginateCvBlocks(measured, availableHeight)
})

const captureHeights = () => {
  measuredHeadingH.value = headingElement?.offsetHeight ?? 0
  measuredContinuationHeadingH.value =
    continuationHeadingElement?.offsetHeight ?? 0

  const heights: number[] = []
  const entries = new Map<number, number[]>()

  for (let i = 0; i < rawBlocks.value.length; i++) {
    const el = sectionElMap.get(i)
    heights.push(el?.offsetHeight ?? 0)

    if (CV_SPLITTABLE_BLOCK_TYPES.includes(rawBlocks.value[i].type)) {
      const els = entryElMap.get(i) ?? []
      entries.set(
        i,
        els.map((e) => e?.offsetHeight ?? 0),
      )
    }
  }

  measuredSections.value = heights
  measuredEntries.value = entries
}

const runMeasureCycle = async () => {
  sectionElMap.clear()
  entryElMap.clear()
  phase.value = 'measure'
  await nextTick()
  await emailComponent?.whenFitted()
  captureHeights()
  phase.value = 'ready'
}

onMounted(async () => {
  // Refs are already populated from the initial render — just measure and switch.
  await nextTick()
  await emailComponent?.whenFitted()
  captureHeights()
  phase.value = 'ready'
})

watch(
  [
    practicalExperiences,
    educationEntries,
    languageSkills,
    hardSkills,
    softSkills,
    hobbies,
    mobility,
  ],
  () => runMeasureCycle(),
  { deep: true },
)

// --- Formatting helpers ---

const formatDate = (date: string) => {
  if (!date) return ''
  try {
    return format(parseISO(date), 'dd. MMMM yyyy', { locale: de })
  } catch {
    return date
  }
}

const formatDateRange = (from: string, to: string | null) => {
  const formatMonth = (d: string) => {
    try {
      return format(parseISO(d), 'MM/yyyy')
    } catch {
      return d
    }
  }
  return `${formatMonth(from)} - ${to ? formatMonth(to) : 'heute'}`
}

const jobTitle = (exp: { jobDescriptions?: string[] }) => {
  if (!exp.jobDescriptions?.length) return null
  const first = exp.jobDescriptions[0]
  const titleMatch = first.match(/^Title:\s*(.+)/)
  return titleMatch ? titleMatch[1] : first
}

const languageName = (code: string) => {
  const option = languageOptions.find((l) => l.code === code)
  return option?.language ?? code
}

defineExpose({ pages, phase })
</script>
